import { useToast } from 'hooks/useToast';
import { apiClient } from 'api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface PersonalityQualificationPayload {
  assessment_id: string;
  personalityAlignments: {
    [key: string]: string;
  };
}

export interface DeletePersonalityQualificationPayload {
  assessment_id: string;
}

/**
 * Hook for submitting personality qualifications
 */
export const usePersonalityQualificationEdit = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const patchMutation = useMutation({
    mutationFn: async (payload: PersonalityQualificationPayload) => {
      const response = await apiClient.patch('/assessment/personality-qualification/', payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['personalityQualifications', assessmentId] });
      openToast({
        show: true,
        heading: "Edit Successful",
        text: "Personality qualifications edited successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to submit personality qualifications';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    submitPersonalityQualification: patchMutation.mutate,
    isSubmitting: patchMutation.isPending,
  };
};

/**
 * Hook for deleting personality qualifications
 */
export const usePersonalityQualificationDelete = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async (payload: DeletePersonalityQualificationPayload) => {
      const response = await apiClient.delete('/assessment/personality-qualification/', {
        data: payload,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personalityQualifications', assessmentId] });
      openToast({
        show: true,
        heading: "Delete Successful",
        text: "Personality qualifications deleted successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to delete personality qualifications';
      openToast({
        show: true,
        heading: "Delete Failed",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    deletePersonalityQualification: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};